import {AsyncPipe, DatePipe} from '@angular/common';
import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {MatIconButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {MatDivider} from '@angular/material/divider';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatProgressBar} from '@angular/material/progress-bar';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatNoDataRow,
    MatRow,
    MatRowDef,
    MatTable,
    MatTableDataSource,
} from '@angular/material/table';
import {MatTooltip} from '@angular/material/tooltip';
import {Client, ClientService} from '@app/admin/client.service';
import {UserRoleDialogComponent} from '@app/admin/user-role-dialog/user-role-dialog.component';
import {deserializeArray} from '@app/serializable';
import {ConfirmDialogComponent} from '@app/shared/confirm-dialog/confirm-dialog.component';
import {IdenticonPipe} from '@app/shared/identicon.pipe';
import {User, UserService} from '@app/shared/user.service';
import {UserFacade} from "@shared/UserFacade";
import {Observable, of} from 'rxjs';
import {catchError, filter, mergeMap, tap} from 'rxjs/operators';
import {environment} from 'src/environments/environment';

const RoleMapping = {
    billing: {
        icon: 'account_balance',
        tooltip:
            'Billing - This user can modify User seats or Storage, but cannot view content and does not count against your seats',
    },
    admin: {
        icon: 'shield',
        tooltip: 'Admin - This user can do everything in this workspace',
    },
    manager: {
        icon: 'account_box',
        tooltip: 'Manager - This user can modify Tags and Streams',
    },
    user: {
        icon: 'person',
        tooltip: '',
    },
};

@Component({
    selector: 'frog-user-admin',
    templateUrl: './user-admin.component.html',
    styleUrls: ['./user-admin.component.scss'],
    imports: [
        MatProgressBar,
        MatFormField,
        MatLabel,
        MatInput,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatCellDef,
        MatCell,
        MatIcon,
        MatTooltip,
        MatIconButton,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        MatDivider,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatNoDataRow,
        AsyncPipe,
        DatePipe,
        IdenticonPipe,
    ],
})
export class UserAdminComponent implements OnInit, AfterViewInit {
    @ViewChild(MatSort) sort: MatSort;

    public displayedColumns: string[];
    public dataSource: MatTableDataSource<User>;
    public client$: Observable<Client>;
    public RoleMapping = RoleMapping;
    public hosted = environment.hosted;

    private _client: Client;

    constructor(
        private clientService: ClientService,
        private userService: UserService,
        private matDialog: MatDialog,
        private matSnackBar: MatSnackBar,
        private userFacade: UserFacade,
    ) {
        this.displayedColumns = ['id', 'role', 'email', 'name', 'last_login', 'actions'];
        this.dataSource = new MatTableDataSource<User>([]);
    }

    ngOnInit(): void {
        this.client$ = this.clientService.client.pipe(
            tap((client) => {
                this._client = client;
                this.dataSource.data = deserializeArray(User, client.members).map((user) => {
                    user.is_owner = user.id === client.owner_id;
                    return user;
                });
            }),
        );
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    userDetail(user: User) {
        this.userFacade.editUser(user)
            .subscribe();
    }

    assignRole(user: User) {
        const dialog = this.matDialog.open(UserRoleDialogComponent, {
            data: user,
        });
        dialog
            .afterClosed()
            .pipe(
                filter((data) => data !== null && data !== '' && data !== undefined),
                mergeMap((data) => {
                    return this.userService.assignRole(user.id, data);
                }),
                mergeMap(() => this.clientService.get()),
                catchError((err) => {
                    this.matSnackBar.open(err.error.detail, null, {
                        duration: 2000,
                        panelClass: ['error-snackbar'],
                    });
                    return of(null);
                }),
            )
            .subscribe();
    }

    removeUser(user: User) {
        if (user.id === this._client.owner_id) {
            this.matSnackBar.open('You cannot remove the owner of the workspace', 'Ok');
            return;
        }
        const dialog = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Confirm Remove User',
                message: `Are you sure you wish to remove ${user.email} from this workspace?`,
                acceptLabel: 'Yes',
                rejectLabel: 'Cancel',
            },
        });
        dialog
            .afterClosed()
            .pipe(
                filter((result) => Boolean(result)),
                mergeMap(() => this.clientService.removeUser(this._client.id, user.id)),
                mergeMap(() => this.clientService.get()),
            )
            .subscribe((client) => {
                this.matSnackBar.open('Successfully removed user from workspace', null, {
                    duration: 2000,
                });
            });
    }
}
